import React from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Order } from '../types';
import { Package, Truck, CheckCircle, AlertTriangle } from 'lucide-react';
const mockOrders: Order[] = [{
  id: '1',
  orderNumber: 'ORD-2023-001',
  supplier: 'Ingram',
  items: ['Dell Latitude 5520', 'Docking Station'],
  totalAmount: 1450.0,
  status: 'shipped',
  trackingNumber: '1Z9992829292',
  orderDate: '24.10.2023',
  estimatedDelivery: '26.10.2023'
}, {
  id: '2',
  orderNumber: 'ORD-2023-002',
  supplier: 'Amazon',
  items: ['Logitech MX Master 3', 'HDMI Kabel 5m'],
  totalAmount: 120.5,
  status: 'delivered',
  trackingNumber: 'DHL0029282',
  orderDate: '20.10.2023',
  estimatedDelivery: '22.10.2023'
}, {
  id: '3',
  orderNumber: 'ORD-2023-003',
  supplier: 'Galaxus',
  items: ['Ubiquiti Dream Machine Pro'],
  totalAmount: 389.0,
  status: 'ordered',
  orderDate: '25.10.2023'
}, {
  id: '4',
  orderNumber: 'ORD-2023-004',
  supplier: 'Böttcher AG',
  items: ['Druckerpapier 50x', 'Toner HP 203A'],
  totalAmount: 450.2,
  status: 'delayed',
  trackingNumber: 'UPS99282',
  orderDate: '18.10.2023',
  estimatedDelivery: '28.10.2023'
}];
interface OrderTrackingProps {
  onLogout: () => void;
}
export function OrderTracking({
  onLogout
}: OrderTrackingProps) {
  const getStatusBadge = (status: Order['status']) => {
    switch (status) {
      case 'ordered':
        return <Badge variant="info" data-id="element-1006">Bestellt</Badge>;
      case 'shipped':
        return <Badge variant="warning" data-id="element-1007">Versendet</Badge>;
      case 'delivered':
        return <Badge variant="success" data-id="element-1008">Geliefert</Badge>;
      case 'delayed':
        return <Badge variant="error" data-id="element-1009">Verzögert</Badge>;
    }
  };
  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'ordered':
        return <Package className="h-5 w-5 text-blue-500" data-id="element-1010" />;
      case 'shipped':
        return <Truck className="h-5 w-5 text-amber-500" data-id="element-1011" />;
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-emerald-500" data-id="element-1012" />;
      case 'delayed':
        return <AlertTriangle className="h-5 w-5 text-red-500" data-id="element-1013" />;
    }
  };
  return <MainLayout title="Bestellsystem & Tracking" onLogout={onLogout} data-id="element-1014">
      <div className="grid grid-cols-1 gap-4" data-id="element-1015">
        {mockOrders.map(order => <Card key={order.id} data-id="element-1016">
            <div className="flex flex-col md:flex-row gap-6" data-id="element-1017">
              {/* Status Icon */}
              <div className="hidden md:flex flex-col items-center justify-center w-16 border-r border-slate-100 pr-6" data-id="element-1018">
                <div className="p-3 bg-slate-50 rounded-full mb-2" data-id="element-1019">
                  {getStatusIcon(order.status)}
                </div>
              </div>

              {/* Order Details */}
              <div className="flex-1" data-id="element-1020">
                <div className="flex justify-between items-start mb-2" data-id="element-1021">
                  <div data-id="element-1022">
                    <h3 className="font-semibold text-slate-900" data-id="element-1023">
                      {order.supplier}
                    </h3>
                    <p className="text-xs text-slate-500" data-id="element-1024">
                      Bestell-Nr: {order.orderNumber}
                    </p>
                  </div>
                  {getStatusBadge(order.status)}
                </div>

                <div className="mt-4" data-id="element-1025">
                  <h4 className="text-sm font-medium text-slate-700 mb-2" data-id="element-1026">
                    Artikel:
                  </h4>
                  <ul className="list-disc list-inside text-sm text-slate-600" data-id="element-1027">
                    {order.items.map((item, idx) => <li key={idx} data-id="element-1028">{item}</li>)}
                  </ul>
                </div>
              </div>

              {/* Tracking Info */}
              <div className="md:w-64 bg-slate-50 rounded-lg p-4 flex flex-col justify-center" data-id="element-1029">
                <div className="text-sm mb-2" data-id="element-1030">
                  <span className="text-slate-500 block" data-id="element-1031">Bestelldatum:</span>
                  <span className="font-medium text-slate-900" data-id="element-1032">
                    {order.orderDate}
                  </span>
                </div>
                {order.estimatedDelivery && <div className="text-sm mb-2" data-id="element-1033">
                    <span className="text-slate-500 block" data-id="element-1034">
                      Lieferung erwartet:
                    </span>
                    <span className={`font-medium ${order.status === 'delayed' ? 'text-red-600' : 'text-slate-900'}`} data-id="element-1035">
                      {order.estimatedDelivery}
                    </span>
                  </div>}
                {order.trackingNumber && <div className="text-sm mt-2 pt-2 border-t border-slate-200" data-id="element-1036">
                    <span className="text-slate-500 block text-xs" data-id="element-1037">
                      Tracking ID:
                    </span>
                    <span className="font-mono text-xs text-slate-700" data-id="element-1038">
                      {order.trackingNumber}
                    </span>
                  </div>}
              </div>
            </div>
          </Card>)}
      </div>
    </MainLayout>;
}